import { MultiSelectItem } from './multi-select-item.js';

/**
 * @class MultiSelect - holds details/summary/ul elements representing multi select element
 */
export class MultiSelect {
  /**
   * @constructor
   * @param {HTMLDetailsElement} element - <details> element
   * @param {Object} options - options object
   */
  constructor(element) {
    if (!element) {
      throw new Error('<details> element is required');
    }

    if (!(element instanceof HTMLDetailsElement)) {
      throw new Error('<details> element is required');
    }

    this.element = element; // the <details> element
    this.summary = this.element.querySelector('summary'); // <summary> element

    // element for showing the selected items
    this.mirror_view = document.createElement('div');
    this.mirror_view.classList.add('dsc-multi-select-mirror-view');

    // checkbox + label
    this.items = Array.from(this.element.querySelectorAll('input[id][type="checkbox"]')).map(checkbox => new MultiSelectItem(checkbox.nextElementSibling, checkbox, this.mirror_view));

    // insert the mirror view
    this.element.after(this.mirror_view);

    // check if we have search search
    this.search_input = this.summary.querySelector('input[type="search"]');

    // event listeners
    this.summary.addEventListener('keyup', event => this.onSpace(event));
    this.element.addEventListener('toggle', event => this.onToggle(event));

    // document listeners
    document.addEventListener('click', event => this.closeOnClick(event));
    document.addEventListener('keydown', event => this.closeOnEscape(event));
  }

  /**
   * @public
   * open details view
   */
  open() {
    if (!this.element.open) {
      this.element.open = true;
    }
  }

  /**
   * @public
   * close details view
   */
  close() {
    if (this.element.open) {
      this.element.open = false;
    }
  }

  /**
   * @private
   * Close on escape handler
   */
  closeOnEscape(event) {
    if (event.keyCode === 27) {
      this.close();
    }
  }

  /**
   * @private
   * Close on outside click and on inner click except when summary is clicked
   */
  closeOnClick(event) {
    const { target } = event;

    if (target === this.summary) {
      return;
    }

    if (this.summary.contains(target)) {
      return;
    }

    this.close();
  }

  /**
   * @private
   * do not close details element if search_input is focused and space is pressed
   */
  onSpace(event) {
    // do nothing if there is no search
    if (!this.search_input) {
      return;
    }

    // we care about space only
    if (event.keyCode !== 32) {
      return;
    }

    // do nothing if search is not focused
    if (document.activeElement !== this.search_input) {
      return;
    }

    // otherwise do not close the <details> element
    event.preventDefault();
  }

  /**
   * @private
   * Toggle handler
   */
  onToggle(event) {
    if (!this.search_input) {
      return;
    }

    if (this.element.open) {
      // focus the search input on open
      this.search_input.focus();
    }
    else {
      // clear the search input on close
      this.search_input.value = '';
      this.search_input.dispatchEvent(new Event('input'));
    }
  }

  /**
   * @static
   * init all multiselects
   * @return Array<MultiSelect> multi selects
   */
  static init() {
    const multi_selects = Array.from(document.querySelectorAll('details.js--multi-select')).map(menu => new MultiSelect(menu));
    return multi_selects;
  }
}
